import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Sidebar } from '@/components/Sidebar';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Send, 
  Mic, 
  MicOff, 
  Bot, 
  Volume2, 
  User,
  Sparkles,
  Settings,
  Play,
  Square
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const safeFormat = (dateStr, fmt, opts) => {
  if (!dateStr) return '';
  try { const d = new Date(dateStr); if (isNaN(d.getTime())) return ''; return format(d, fmt, opts); } catch { return ''; }
};

export default function AIAssistantPage() {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState('nova');
  const [showSettings, setShowSettings] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [stats, setStats] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  const voices = [
    { value: 'nova', label: 'Nova (Feminina, Amigável)' },
    { value: 'shimmer', label: 'Shimmer (Feminina, Alegre)' },
    { value: 'alloy', label: 'Alloy (Neutra)' },
    { value: 'echo', label: 'Echo (Masculina, Calma)' },
    { value: 'onyx', label: 'Onyx (Masculina, Autoritária)' },
  ];

  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchSettings();
    const id = setInterval(fetchStats, 30000);
    return () => clearInterval(id);
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ai/settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setAiEnabled(!!res.data?.enabled);
    } catch {
      // silent
    }
  };

  const handleToggleAI = async (checked) => {
    setAiEnabled(checked);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/ai/settings`,
        { enabled: checked, voice: selectedVoice, auto_respond: true, response_delay_seconds: 2 },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(checked ? 'IA ativada' : 'IA desativada — nao vai mais responder leads automaticamente');
    } catch (e) {
      setAiEnabled(!checked);
      toast.error('Erro ao salvar configuracao da IA');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ai/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setStats(res.data);
    } catch {
      // silently ignore
    }
  };

  useEffect(() => {
    if (selectedLead) {
      fetchMessages(selectedLead.id);
    }
  }, [selectedLead]);

  const fetchLeads = async () => {
    try {
      const response = await api.getLeads();
      // Handle paginated response - extract leads array
      const leadsData = Array.isArray(response.data) ? response.data : (response.data?.leads || []);
      setLeads(leadsData);
      if (leadsData.length > 0 && !selectedLead) {
        setSelectedLead(leadsData[0]);
      }
    } catch (error) {
      toast.error('Erro ao carregar leads');
    }
  };

  const fetchMessages = async (leadId) => {
    try {
      const response = await api.getMessages(leadId);
      setMessages(response.data);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedLead || isProcessing) return;

    setIsProcessing(true);
    const messageToSend = newMessage;
    setNewMessage('');

    try {
      // Gerar resposta da IA
      const response = await api.generateAIResponse(selectedLead.id, messageToSend, true);
      
      // Atualizar mensagens
      await fetchMessages(selectedLead.id);
      
      // Tocar áudio da resposta se disponível
      if (response.data.audio_base64) {
        playAudioBase64(response.data.audio_base64);
      }
      
      toast.success('Resposta gerada pela IA!');
    } catch (error) {
      toast.error('Erro ao gerar resposta');
    } finally {
      setIsProcessing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mp3' });
        stream.getTracks().forEach(track => track.stop());
        await processAudioMessage(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Gravando... Clique novamente para parar');
    } catch (error) {
      toast.error('Erro ao acessar microfone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudioMessage = async (audioBlob) => {
    if (!selectedLead) return;

    setIsProcessing(true);
    try {
      const file = new File([audioBlob], 'audio.mp3', { type: 'audio/mp3' });
      const response = await api.processAudioMessage(selectedLead.id, file);
      
      // Atualizar mensagens
      await fetchMessages(selectedLead.id);
      
      // Mostrar transcrição
      if (response.data.transcribed_text) {
        toast.info(`Você disse: "${response.data.transcribed_text}"`);
      }
      
      // Tocar áudio da resposta
      if (response.data.audio_base64) {
        playAudioBase64(response.data.audio_base64);
      }
      
      toast.success('Áudio processado pela IA!');
    } catch (error) {
      toast.error('Erro ao processar áudio');
    } finally {
      setIsProcessing(false);
    }
  };

  const playAudioBase64 = (base64Audio) => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
      audioRef.current = audio;
      
      audio.onplay = () => setIsPlaying(true);
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => {
        setIsPlaying(false);
        toast.error('Erro ao reproduzir áudio');
      };
      
      audio.play();
      setCurrentAudio(base64Audio);
    } catch (error) {
      toast.error('Erro ao reproduzir áudio');
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-7xl mx-auto h-[calc(100vh-8rem)]">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-100">
                  <Sparkles className="w-6 h-6 text-violet-600" />
                </div>
                <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Assistente IA
                </h1>
              </div>
              <p className="text-slate-600 mt-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Resgate automatico de leads inativos com Claude Sonnet 4.6
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={aiEnabled}
                  onCheckedChange={handleToggleAI}
                  id="ai-enabled"
                />
                <Label htmlFor="ai-enabled" className="text-sm">IA Ativa</Label>
              </div>
              
              <Button
                variant="outline"
                onClick={() => setShowSettings(!showSettings)}
                data-testid="ai-settings-button"
              >
                <Settings className="w-4 h-4 mr-2" />
                Configurações
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <Card className="p-4">
                <p className="text-xs text-slate-500">Leads alcancados</p>
                <p className="text-2xl font-bold text-violet-700">{stats.total_leads_reached}</p>
                <p className="text-xs text-emerald-600 mt-1">{stats.leads_reached_last_24h} nas 24h</p>
              </Card>
              <Card className="p-4">
                <p className="text-xs text-slate-500">Mensagens enviadas</p>
                <p className="text-2xl font-bold text-violet-700">{stats.total_messages_sent}</p>
              </Card>
              <Card className="p-4">
                <p className="text-xs text-slate-500">Ultimas 24h</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.messages_last_24h}</p>
              </Card>
              <Card className="p-4">
                <p className="text-xs text-slate-500">Ultimos 7 dias</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.messages_last_7d}</p>
              </Card>
              <Card className="p-4">
                <p className="text-xs text-slate-500">Hot leads gerados</p>
                <p className="text-2xl font-bold text-orange-600">{stats.hot_leads_generated}</p>
              </Card>
            </div>
          )}

          {/* Settings Panel */}
          {showSettings && (
            <Card className="p-6 mb-6 border-violet-200 bg-violet-50/50">
              <h3 className="font-semibold text-slate-900 mb-4">Configurações da IA</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Voz da Sofia</Label>
                  <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {voices.map(voice => (
                        <SelectItem key={voice.value} value={voice.value}>
                          {voice.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Modelo de IA</Label>
                  <Input value="GPT-5.2 (OpenAI)" disabled className="mt-1 bg-white" />
                </div>
                <div>
                  <Label>Transcrição</Label>
                  <Input value="Whisper (OpenAI)" disabled className="mt-1 bg-white" />
                </div>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100%-10rem)]">
            {/* Leads List */}
            <Card className="p-0 overflow-hidden">
              <div className="p-4 border-b bg-white">
                <h3 className="font-semibold text-slate-900">Leads para Atender</h3>
              </div>
              <ScrollArea className="h-[calc(100%-4rem)]">
                {leads.map((lead) => (
                  <div
                    key={lead.id}
                    onClick={() => setSelectedLead(lead)}
                    className={`p-4 border-b cursor-pointer transition-colors ${
                      selectedLead?.id === lead.id
                        ? 'bg-violet-50 border-l-4 border-l-violet-600'
                        : 'hover:bg-slate-50'
                    }`}
                    data-testid={`ai-lead-${lead.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback className="bg-violet-100 text-violet-700">
                          {lead.name.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{lead.name}</p>
                        <p className="text-sm text-slate-500 truncate">{lead.phone}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </Card>

            {/* Chat Area */}
            <Card className="lg:col-span-2 p-0 overflow-hidden flex flex-col">
              {selectedLead ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b bg-white flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback className="bg-violet-100 text-violet-700">
                          {selectedLead.name.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-semibold text-slate-900">{selectedLead.name}</h3>
                        <p className="text-sm text-slate-500">{selectedLead.phone}</p>
                      </div>
                    </div>
                    
                    {aiEnabled && (
                      <Badge className="bg-violet-100 text-violet-700">
                        <Bot className="w-3 h-3 mr-1" />
                        IA Ativa
                      </Badge>
                    )}
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-6 bg-slate-50">
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.is_from_lead ? 'justify-start' : 'justify-end'}`}
                          data-testid={`ai-message-${msg.id}`}
                        >
                          <div className="flex items-end gap-2 max-w-[70%]">
                            {msg.is_from_lead && (
                              <Avatar className="w-8 h-8">
                                <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                                  <User className="w-4 h-4" />
                                </AvatarFallback>
                              </Avatar>
                            )}
                            
                            <div
                              className={`rounded-lg p-3 ${
                                msg.is_from_lead
                                  ? 'bg-white text-slate-900'
                                  : msg.is_ai_response
                                  ? 'bg-gradient-to-br from-violet-600 to-violet-700 text-white'
                                  : 'bg-emerald-600 text-white'
                              }`}
                            >
                              {msg.is_ai_response && (
                                <div className="flex items-center gap-1 mb-1 opacity-80">
                                  <Bot className="w-3 h-3" />
                                  <span className="text-xs">Sofia</span>
                                </div>
                              )}
                              <p>{msg.message}</p>
                              <div className="flex items-center justify-between mt-1">
                                <p className={`text-xs ${
                                  msg.is_from_lead ? 'text-slate-500' : 'opacity-70'
                                }`}>
                                  {safeFormat(msg.created_at, 'HH:mm', { locale: ptBR })}
                                </p>
                                {msg.has_audio && (
                                  <Volume2 className="w-3 h-3 opacity-70" />
                                )}
                              </div>
                            </div>
                            
                            {!msg.is_from_lead && msg.is_ai_response && (
                              <Avatar className="w-8 h-8">
                                <AvatarFallback className="bg-violet-100 text-violet-600 text-xs">
                                  <Bot className="w-4 h-4" />
                                </AvatarFallback>
                              </Avatar>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {isProcessing && (
                        <div className="flex justify-end">
                          <div className="bg-violet-100 rounded-lg p-3 flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600" />
                            <span className="text-sm text-violet-700">Sofia está pensando...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>

                  {/* Audio Player */}
                  {isPlaying && (
                    <div className="px-4 py-2 bg-violet-50 border-t flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Volume2 className="w-4 h-4 text-violet-600 animate-pulse" />
                        <span className="text-sm text-violet-700">Sofia está falando...</span>
                      </div>
                      <Button size="sm" variant="ghost" onClick={stopAudio}>
                        <Square className="w-4 h-4" />
                      </Button>
                    </div>
                  )}

                  {/* Input */}
                  <form onSubmit={handleSendMessage} className="p-4 border-t bg-white">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={isRecording ? 'destructive' : 'outline'}
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={isProcessing}
                        className={isRecording ? 'animate-pulse' : ''}
                        data-testid="record-audio-button"
                      >
                        {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                      </Button>
                      
                      <Input
                        placeholder="Digite uma mensagem para a IA responder..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        disabled={isProcessing || isRecording}
                        data-testid="ai-message-input"
                      />
                      
                      <Button
                        type="submit"
                        disabled={isProcessing || !newMessage.trim() || isRecording}
                        className="bg-violet-600 hover:bg-violet-700"
                        data-testid="send-ai-message-button"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <p className="text-xs text-slate-500 mt-2">
                      {isRecording 
                        ? '🎙️ Gravando áudio... Clique no microfone para parar'
                        : '💡 Envie texto ou grave áudio - A Sofia responderá com voz!'}
                    </p>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-500">
                  <div className="text-center">
                    <Bot className="w-16 h-16 mx-auto mb-4 text-violet-300" />
                    <p>Selecione um lead para conversar com a IA</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
